import React, { useEffect, useState, useRef } from 'react';
import ReactDOM from 'react-dom';

const BookingOverlay = ({ isOpen, onClose }) => {
  const [shouldRender, setShouldRender] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    location: '',
    notes: ''
  });
  const [predictions, setPredictions] = useState([]);
  const [showPredictions, setShowPredictions] = useState(false);
  const [status, setStatus] = useState(''); // '', 'sending', 'success', 'error'
  const [errorMessage, setErrorMessage] = useState('');
  const autocompleteService = useRef(null);
  const placesService = useRef(null);
  const locationInputRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      setShouldRender(true);
      setStatus('');
      setErrorMessage('');
      const timer = setTimeout(() => setIsVisible(true), 50);
      return () => clearTimeout(timer);
    } else {
      setIsVisible(false);
      const timer = setTimeout(() => setShouldRender(false), 500);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  // Initialize Google Places services when overlay opens
  useEffect(() => {
    if (isOpen && window.google && window.google.maps && window.google.maps.places) {
      autocompleteService.current = new window.google.maps.places.AutocompleteService();
      placesService.current = new window.google.maps.places.PlacesService(
        document.createElement('div')
      );
    }
  }, [isOpen]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleLocationChange = (e) => {
    const value = e.target.value;
    setFormData(prev => ({ ...prev, location: value }));

    if (value.length > 2 && autocompleteService.current) {
      autocompleteService.current.getPlacePredictions(
        { input: value },
        (results, status) => {
          if (status === window.google.maps.places.PlacesServiceStatus.OK && results) {
            setPredictions(results);
            setShowPredictions(true);
          } else {
            setPredictions([]);
            setShowPredictions(false);
          }
        }
      );
    } else {
      setPredictions([]);
      setShowPredictions(false);
    }
  };

  const handleSelectPlace = (placeId, description) => {
    setFormData(prev => ({ ...prev, location: description }));
    setPredictions([]);
    setShowPredictions(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('sending');
    setErrorMessage('');

    try {
      const response = await fetch('/api/send', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      // Check if response is JSON
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        throw new Error('Server returned non-JSON response');
      }

      const result = await response.json();

      if (response.ok) {
        setStatus('success');
        // Reset form after short delay
        setTimeout(() => {
          setFormData({ name: '', email: '', location: '', notes: '' });
          setStatus('');
          onClose();
        }, 2000);
      } else {
        setStatus('error');
        setErrorMessage(result.error || 'Failed to send');
      }
    } catch (error) {
      console.error('Submission error:', error);
      setStatus('error');
      setErrorMessage('Failed to send. Please try again.');
    }
  };

  if (!shouldRender) return null;

  const overlayContent = (
    <div
      className={`transition-opacity duration-500 ease-in-out ${isVisible ? 'opacity-100' : 'opacity-0'
        }`}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        width: '100vw',
        height: '100vh',
        backgroundColor: '#000000',
        zIndex: 10000,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        fontFamily: 'Montserrat, sans-serif',
        margin: 0,
        padding: '20px',
        boxSizing: 'border-box'
      }}
    >
      {/* Close Button */}
      <button
        onClick={onClose}
        className="hover:opacity-70 transition-opacity duration-300"
        style={{
          position: 'absolute',
          top: '48px',
          right: '48px',
          background: 'none',
          border: 'none',
          color: '#ffffff',
          fontSize: '32px',
          cursor: 'pointer',
          fontFamily: 'Montserrat, sans-serif',
          fontWeight: 300,
          padding: 0,
          lineHeight: 1
        }}
        aria-label="Close booking form"
      >
        ×
      </button>

      {/* Form Container */}
      <div
        className={`transition-all duration-500 ease-out ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
          }`}
        style={{
          width: '100%',
          maxWidth: '500px'
        }}
      >
        {/* Title */}
        <h2
          style={{
            color: '#ffffff',
            fontSize: '32px',
            fontWeight: 700,
            textAlign: 'center',
            marginBottom: '40px',
            letterSpacing: '0.1em',
            textTransform: 'uppercase'
          }}
        >
          Bookings
        </h2>

        {/* Form */}
        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
          {/* Name Input */}
          <div>
            <input
              type="text"
              name="name"
              placeholder="Your Name"
              value={formData.name}
              onChange={handleInputChange}
              required
              style={{
                width: '100%',
                padding: '16px 0',
                backgroundColor: 'transparent',
                border: 'none',
                borderBottom: '1px solid rgba(255, 255, 255, 0.3)',
                color: '#ffffff',
                fontSize: '16px',
                fontFamily: 'Montserrat, sans-serif',
                outline: 'none',
                transition: 'border-color 0.3s ease'
              }}
              onFocus={(e) => e.target.style.borderBottomColor = '#ffffff'}
              onBlur={(e) => e.target.style.borderBottomColor = 'rgba(255, 255, 255, 0.3)'}
            />
          </div>

          {/* Email Input */}
          <div>
            <input
              type="email"
              name="email"
              placeholder="Email Address"
              value={formData.email}
              onChange={handleInputChange}
              required
              style={{
                width: '100%',
                padding: '16px 0',
                backgroundColor: 'transparent',
                border: 'none',
                borderBottom: '1px solid rgba(255, 255, 255, 0.3)',
                color: '#ffffff',
                fontSize: '16px',
                fontFamily: 'Montserrat, sans-serif',
                outline: 'none',
                transition: 'border-color 0.3s ease'
              }}
              onFocus={(e) => e.target.style.borderBottomColor = '#ffffff'}
              onBlur={(e) => e.target.style.borderBottomColor = 'rgba(255, 255, 255, 0.3)'}
            />
          </div>

          {/* Location Input with Google Places Autocomplete */}
          <div style={{ position: 'relative' }}>
            <input
              ref={locationInputRef}
              type="text"
              name="location"
              placeholder="Location"
              value={formData.location}
              onChange={handleLocationChange}
              onFocus={() => predictions.length > 0 && setShowPredictions(true)}
              onBlur={(e) => {
                e.target.style.borderBottomColor = 'rgba(255, 255, 255, 0.3)';
                // Delay hiding to allow click on predictions
                setTimeout(() => setShowPredictions(false), 200);
              }}
              required
              autoComplete="off"
              style={{
                width: '100%',
                padding: '16px 0',
                backgroundColor: 'transparent',
                border: 'none',
                borderBottom: '1px solid rgba(255, 255, 255, 0.3)',
                color: '#ffffff',
                fontSize: '16px',
                fontFamily: 'Montserrat, sans-serif',
                outline: 'none',
                transition: 'border-color 0.3s ease'
              }}
            />
            {/* Location Predictions Dropdown */}
            {showPredictions && predictions.length > 0 && (
              <div
                style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  right: 0,
                  backgroundColor: '#000000',
                  border: '1px solid rgba(255, 255, 255, 0.3)',
                  zIndex: 100,
                  maxHeight: '200px',
                  overflowY: 'auto'
                }}
              >
                {predictions.map((prediction) => (
                  <div
                    key={prediction.place_id}
                    onClick={() => handleSelectPlace(prediction.place_id, prediction.description)}
                    style={{
                      padding: '12px 16px',
                      color: '#ffffff',
                      fontSize: '14px',
                      fontFamily: 'Montserrat, sans-serif',
                      cursor: 'pointer',
                      transition: 'background-color 0.2s ease'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#36454F'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                  >
                    {prediction.description}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Notes Textarea */}
          <div>
            <textarea
              name="notes"
              placeholder="Additional Notes (optional)"
              value={formData.notes}
              onChange={handleInputChange}
              rows={3}
              style={{
                width: '100%',
                padding: '16px 0',
                backgroundColor: 'transparent',
                border: 'none',
                borderBottom: '1px solid rgba(255, 255, 255, 0.3)',
                color: '#ffffff',
                fontSize: '16px',
                fontFamily: 'Montserrat, sans-serif',
                outline: 'none',
                resize: 'none',
                transition: 'border-color 0.3s ease'
              }}
              onFocus={(e) => e.target.style.borderBottomColor = '#ffffff'}
              onBlur={(e) => e.target.style.borderBottomColor = 'rgba(255, 255, 255, 0.3)'}
            />
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={status === 'sending'}
            className="transition-all duration-300"
            style={{
              marginTop: '16px',
              padding: '16px 32px',
              backgroundColor: status === 'success' ? '#22c55e' : status === 'error' ? '#ef4444' : '#36454F',
              border: 'none',
              color: '#ffffff',
              fontSize: '14px',
              fontWeight: 700,
              fontFamily: 'Montserrat, sans-serif',
              letterSpacing: '0.15em',
              textTransform: 'uppercase',
              cursor: status === 'sending' ? 'not-allowed' : 'pointer',
              opacity: status === 'sending' ? 0.7 : 1,
              transition: 'background-color 0.3s ease'
            }}
            onMouseOver={(e) => {
              if (status !== 'sending' && status !== 'success' && status !== 'error') {
                e.currentTarget.style.backgroundColor = '#ffffff';
                e.currentTarget.style.color = '#000000';
              }
            }}
            onMouseOut={(e) => {
              if (status !== 'sending' && status !== 'success' && status !== 'error') {
                e.currentTarget.style.backgroundColor = '#36454F';
                e.currentTarget.style.color = '#ffffff';
              }
            }}
          >
            {status === 'sending' ? 'Sending...' :
              status === 'success' ? 'Sent!' :
                status === 'error' ? 'Try Again' :
                  'Submit Request'}
          </button>

          {/* Status Message */}
          {status === 'error' && errorMessage && (
            <p style={{
              color: '#ef4444',
              fontSize: '14px',
              textAlign: 'center',
              marginTop: '12px',
              fontFamily: 'Montserrat, sans-serif'
            }}>
              {errorMessage}
            </p>
          )}
          {status === 'success' && (
            <p style={{
              color: '#22c55e',
              fontSize: '14px',
              textAlign: 'center',
              marginTop: '12px',
              fontFamily: 'Montserrat, sans-serif'
            }}>
              Your request has been sent successfully!
            </p>
          )}
        </form>
      </div>
    </div>
  );

  return ReactDOM.createPortal(overlayContent, document.body);
};

export default BookingOverlay;
